import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IndianRupee, BadgePercent, ArrowUpCircle,
  Calendar, Loader2, InboxIcon,
} from 'lucide-react';
import { investmentAPI } from '../api/investment';
import { withdrawalAPI } from '../api/withdrawal';
import { Card } from '../components/ui/Card';
import StatusBadge from '../components/ui/StatusBadge';
import PageHeader from '../components/ui/PageHeader';

// ── helpers ───────────────────────────────────────────────────
const fmt = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const fmtDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const INVEST_LABEL = { deposit: 'Deposit', profit: 'Profit Interest' };

const TAB_ACTIVE   = { background: 'linear-gradient(135deg,#d4a017,#f0c040)', color: '#fff', boxShadow: '0 2px 10px rgba(212,160,23,0.3)' };
const TAB_INACTIVE = { color: 'rgba(255,255,255,0.5)' };

// ── item cards ────────────────────────────────────────────────
function InvestCard({ entry, index }) {
  const isProfit = entry.type === 'profit';
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 6) * 0.04 }}
    >
      <Card className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: isProfit ? 'rgba(99,102,241,0.18)' : 'rgba(212,160,23,0.15)' }}
        >
          {isProfit
            ? <BadgePercent size={18} color="#a5b4fc" />
            : <IndianRupee  size={18} color="#f0c040" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {INVEST_LABEL[entry.type] ?? entry.type}
          </p>
          <p className="text-xs flex items-center gap-1 mt-0.5"
             style={{ color: 'rgba(255,255,255,0.4)' }}>
            <Calendar size={10} />
            {fmtDate(entry.date)}
          </p>
          {entry.note && (
            <p className="text-[11px] mt-0.5 truncate italic"
               style={{ color: 'rgba(255,255,255,0.35)' }}>
              {entry.note}
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-white">{fmt(entry.amount)}</p>
          <StatusBadge status={entry.status} />
        </div>
      </Card>
    </motion.div>
  );
}

function WithdrawalCard({ entry, index }) {
  const periodLabel = entry.withdrawal_period
    ? entry.withdrawal_period.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 6) * 0.04 }}
    >
      <Card className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(5,150,105,0.18)' }}
        >
          <ArrowUpCircle size={18} color="#6ee7b7" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            Withdrawal
          </p>
          <p className="text-xs flex items-center gap-1 mt-0.5"
             style={{ color: 'rgba(255,255,255,0.4)' }}>
            <Calendar size={10} />
            {fmtDate(entry.requested_at)}
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {periodLabel}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-white">{fmt(entry.amount)}</p>
          <StatusBadge status={entry.status} />
        </div>
      </Card>
    </motion.div>
  );
}

function CardSkeleton() {
  return (
    <div className="card flex items-center gap-3">
      <div className="skeleton w-10 h-10 rounded-xl flex-shrink-0" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="skeleton h-3.5 w-28 rounded" />
        <div className="skeleton h-3 w-20 rounded" />
      </div>
      <div className="flex flex-col gap-1.5 items-end">
        <div className="skeleton h-3.5 w-16 rounded" />
        <div className="skeleton h-4 w-14 rounded-full" />
      </div>
    </div>
  );
}

function LoadMoreButton({ loading, onClick }) {
  return (
    <motion.button
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      disabled={loading}
      className="w-full flex items-center justify-center gap-2 rounded-2xl py-3.5
                 text-sm font-semibold transition-all disabled:opacity-50"
      style={{
        background: 'rgba(212,160,23,0.12)',
        border: '1px solid rgba(212,160,23,0.3)',
        color: '#f0c040',
      }}
    >
      {loading
        ? <><Loader2 size={15} className="animate-spin" /> Loading…</>
        : 'Load More'}
    </motion.button>
  );
}

function EmptyState({ message }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Card className="flex flex-col items-center gap-3 py-12">
        <div className="w-14 h-14 rounded-full flex items-center justify-center"
             style={{ background: 'rgba(255,255,255,0.06)' }}>
          <InboxIcon size={24} style={{ color: 'rgba(255,255,255,0.25)' }} />
        </div>
        <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {message}
        </p>
      </Card>
    </motion.div>
  );
}

// ── Invest tab ────────────────────────────────────────────────
function InvestTab() {
  const [items,      setItems]      = useState([]);
  const [page,       setPage]       = useState(1);
  const [lastPage,   setLastPage]   = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,      setError]      = useState('');

  const fetchPage = useCallback(async (pg, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError('');
    try {
      const res = await investmentAPI.getHistory({ page: pg, per_page: 10 });
      const { history, pagination } = res.data.data;
      setItems((prev) => append ? [...prev, ...(history ?? [])] : (history ?? []));
      setPage(pagination.current_page);
      setLastPage(pagination.last_page);
    } catch {
      setError('Failed to load investments.');
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(1); }, [fetchPage]);

  if (loading) return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4].map((i) => <CardSkeleton key={i} />)}
    </div>
  );

  if (error) return (
    <p className="text-sm text-center py-8" style={{ color: '#fca5a5' }}>{error}</p>
  );

  if (!items.length) return <EmptyState message="No investment transactions yet" />;

  return (
    <div className="flex flex-col gap-3">
      {items.map((entry, idx) => (
        <InvestCard key={entry.id ?? idx} entry={entry} index={idx} />
      ))}
      {page < lastPage && (
        <LoadMoreButton loading={loadingMore} onClick={() => fetchPage(page + 1, true)} />
      )}
      {page >= lastPage && items.length > 0 && (
        <p className="text-center text-xs py-2" style={{ color: 'rgba(255,255,255,0.25)' }}>
          All transactions loaded
        </p>
      )}
    </div>
  );
}

// ── Withdrawal tab ────────────────────────────────────────────
function WithdrawalTab() {
  const [items,       setItems]       = useState([]);
  const [page,        setPage]        = useState(1);
  const [lastPage,    setLastPage]    = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState('');

  const fetchPage = useCallback(async (pg, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError('');
    try {
      const res = await withdrawalAPI.getHistory({ page: pg, per_page: 10 });
      const { withdrawals, pagination } = res.data.data;
      setItems((prev) => append ? [...prev, ...(withdrawals ?? [])] : (withdrawals ?? []));
      setPage(pagination.current_page);
      setLastPage(pagination.last_page);
    } catch {
      setError('Failed to load withdrawals.');
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(1); }, [fetchPage]);

  if (loading) return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4].map((i) => <CardSkeleton key={i} />)}
    </div>
  );

  if (error) return (
    <p className="text-sm text-center py-8" style={{ color: '#fca5a5' }}>{error}</p>
  );

  if (!items.length) return <EmptyState message="No withdrawal transactions yet" />;

  return (
    <div className="flex flex-col gap-3">
      {items.map((entry, idx) => (
        <WithdrawalCard key={entry.id ?? idx} entry={entry} index={idx} />
      ))}
      {page < lastPage && (
        <LoadMoreButton loading={loadingMore} onClick={() => fetchPage(page + 1, true)} />
      )}
      {page >= lastPage && items.length > 0 && (
        <p className="text-center text-xs py-2" style={{ color: 'rgba(255,255,255,0.25)' }}>
          All transactions loaded
        </p>
      )}
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────
export default function TransactionHistoryPage() {
  const [tab, setTab] = useState('invest');

  return (
    <div>
      <PageHeader title="Transaction History" subtitle="All your financial activity" showBack />

      <div className="px-4 pt-3 pb-6 flex flex-col gap-4">

        {/* Tab switcher */}
        <div
          className="flex gap-1 rounded-2xl p-1"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          {[
            { key: 'invest',    label: 'Investments' },
            { key: 'withdrawal', label: 'Withdrawals' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className="flex-1 py-2.5 text-sm font-semibold rounded-xl transition-all"
              style={tab === key ? TAB_ACTIVE : TAB_INACTIVE}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab content — each tab manages its own state so it survives switching */}
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {tab === 'invest'
              ? <InvestTab />
              : <WithdrawalTab />}
          </motion.div>
        </AnimatePresence>

      </div>
    </div>
  );
}
